import type { Ref } from 'react';
import { createContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { LinkProps as AriaLinkProps } from 'react-aria-components/Link';
import { Link as AriaLink } from 'react-aria-components/Link';
import type { ContextValue } from 'react-aria-components/slots';
import type { DOMProps } from '@react-types/shared';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { useLPContextProps } from './utils';

import styles from './styles/Link.module.css';

const linkStyles = cva(styles.base, {
	variants: {
		variant: {
			default: styles.default,
			subtle: styles.subtle,
		},
		underline: {
			always: styles.underlineAlways,
			hover: styles.underlineHover,
			none: styles.underlineNone,
		},
	},
	defaultVariants: {
		variant: 'default',
		underline: 'hover',
	},
});

interface LinkProps extends AriaLinkProps, VariantProps<typeof linkStyles>, DOMProps {
	ref?: Ref<HTMLAnchorElement>;
	/** Controls when the link is underlined. */
	underline?: 'always' | 'hover' | 'none';
}

const LinkContext = createContext<ContextValue<LinkProps, HTMLAnchorElement>>(null);

/**
 * A link allows a user to navigate to another page or resource within a web page or application.
 *
 * https://react-spectrum.adobe.com/react-aria/Link.html
 */
const Link = ({ ref, ...props }: LinkProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, LinkContext);
	const { variant = 'default', underline = 'hover' } = mergedProps;

	return (
		<AriaLink
			{...mergedProps}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				linkStyles({ ...renderProps, variant, underline, className }),
			)}
		/>
	);
};

export { Link, LinkContext, linkStyles };
export type { LinkProps };
